import React, { useState } from 'react';
import { formRowSx } from '../utils/panelSurfaces';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, TextField, Select, MenuItem,
  FormControl
} from '@mui/material';

// The Add-value box holds a multi-digit ordinal (a nakshatra runs to 27, and Group 1's rows
// are seeded with values a user then edits). At 56px a three-digit entry was clipped by the
// number spinner, so the figure being submitted could not be read back. 84 = 56 x 1.5.
const ADD_VALUE_WIDTH = 84;

const PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu',
  'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta',
  'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha', 'Mula', 'Purva Ashadha',
  'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha', 'Purva Bhadrapada',
  'Uttara Bhadrapada', 'Revati'
];
const HEMISPHERES = ['East', 'West'];

const WIN_DEFAULTS = { nDays: '7', windowMode: 'before' };

const initForms = {
  r1:  { planet: 'Sun',     longitude: '',   sign: 'Aries',    ...WIN_DEFAULTS, value: '2' },
  r2:  { planet1: 'Sun',   planet2: 'Moon', diff: '',          ...WIN_DEFAULTS, value: '2' },
  r3:  { planet1: 'Sun',   planet2: 'Moon', diff: '',          ...WIN_DEFAULTS, value: '2' },
  r4:  { planet: 'Sun',    longitude: '',                      ...WIN_DEFAULTS, value: '2' },
  r5:  { planet: 'Sun',    declination: '',                    ...WIN_DEFAULTS, value: '2' },
  r6:  { planet: 'Sun',    sign: 'Aries',                      value: '2' },
  r7:  { planet: 'Sun',    sign: 'Aries',                      value: '2' },
  r8:  { planet: 'Moon',   nakshatra: 'Ashwini',               value: '2' },
  r9:  { planet: 'Moon',   hemisphere: 'East',                 value: '2' },
  r10: { planet: 'Moon',   hemisphere: 'East',                 value: '2' },
  r11: { planet: 'Mercury',hemisphere: 'East',                 value: '2' },
  r12: { planet: 'Mercury',hemisphere: 'East',                 value: '2' },
};

const FIELD_SX = { width: 80 };
const PLANET_SX = { minWidth: 110 };
const SIGN_SX = { minWidth: 130 };
const NAKSH_SX = { minWidth: 160 };
const HEMI_SX = { minWidth: 90 };
const VALUE_SX = { width: ADD_VALUE_WIDTH };

const PlanetSelect = ({ value, onChange, sx, exclude }) => (
  <FormControl size="small" sx={sx || PLANET_SX}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {PLANETS.filter(p => !exclude || !exclude.includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
    </Select>
  </FormControl>
);
PlanetSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, sx: PropTypes.object, exclude: PropTypes.array };

const SignSelect = ({ value, onChange, sx }) => (
  <FormControl size="small" sx={sx || SIGN_SX}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {SIGNS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
    </Select>
  </FormControl>
);
SignSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, sx: PropTypes.object };

const HemiSelect = ({ value, onChange }) => (
  <FormControl size="small" sx={HEMI_SX}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {HEMISPHERES.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
    </Select>
  </FormControl>
);
HemiSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const Label = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{children}</Typography>
);
Label.propTypes = { children: PropTypes.node.isRequired };

const NumField = ({ value, onChange, sx }) => (
  <TextField
    size="small"
    type="number"
    value={value}
    onChange={e => onChange(e.target.value)}
    sx={sx || FIELD_SX}
  />
);
NumField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, sx: PropTypes.object };

const LonField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value}
    onChange={e => onChange(e.target.value)}
    inputProps={{ min: 0, max: 359.99, step: 0.01 }}
    sx={FIELD_SX} />
);
LonField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const DeclField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value}
    onChange={e => onChange(e.target.value)}
    inputProps={{ min: -40, max: 40, step: 0.01 }}
    sx={FIELD_SX} />
);
DeclField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddValueField = ({ value, onChange }) => (
  <TextField
    size="small"
    type="number"
    value={value}
    onChange={e => onChange(e.target.value)}
    inputProps={{ min: 1, step: 1 }}
    sx={VALUE_SX}
  />
);
AddValueField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddBtn = ({ onClick, disabled }) => (
  <Button
    variant="contained"
    size="small"
    onClick={onClick}
    disabled={disabled}
    sx={{ minWidth: 56, px: 1.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
  >
    ADD
  </Button>
);
AddBtn.propTypes = { onClick: PropTypes.func.isRequired, disabled: PropTypes.bool };

const WINDOW_MODE_OPTIONS = [
  { value: 'before', label: 'From specified number of days before the event to the event' },
  { value: 'after',  label: 'From the event to the specified number of days after' },
  { value: 'around', label: 'From specified number of days before the event to the event to after the event' },
];

const EventWindowControls = ({ nDays, windowMode, onNDaysChange, onModeChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
    <TextField size="small" type="number" value={nDays} onChange={e => onNDaysChange(e.target.value)}
      inputProps={{ min: 1, step: 1 }} sx={{ width: 64 }} />
    <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>days</Typography>
    <FormControl size="small" sx={{ minWidth: 420 }}>
      <Select value={windowMode} onChange={e => onModeChange(e.target.value)}>
        {WINDOW_MODE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  </Box>
);
EventWindowControls.propTypes = {
  nDays: PropTypes.string.isRequired, windowMode: PropTypes.string.isRequired,
  onNDaysChange: PropTypes.func.isRequired, onModeChange: PropTypes.func.isRequired,
};

const CriteriaRow = ({ children }) => (
  <Box sx={(theme) => ({
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
    borderRadius: 1, p: 1, mb: 1, ...formRowSx('tinted')(theme)
  })}>
    {children}
  </Box>
);
CriteriaRow.propTypes = { children: PropTypes.node.isRequired };

const MOAGroup1Tab = ({ onAddCondition, forms: propForms, onFormsChange }) => {
  const [localForms, setLocalForms] = useState(initForms);
  const forms = propForms || localForms;
  const setForms = onFormsChange || setLocalForms;

  const set = (row, field, val) => setForms(f => ({ ...f, [row]: { ...f[row], [field]: val } }));
  const setPair = (row, f1, v, f2) => setForms(prev => {
    const updated = { ...prev[row], [f1]: v };
    if (updated[f2] === v) updated[f2] = PLANETS.find(p => p !== v) || PLANETS[0];
    return { ...prev, [row]: updated };
  });

  const add = (type, condObj) => {
    const val = parseInt(condObj.value, 10);
    if (!val || val < 1) return;
    onAddCondition({ group: 1, type, ...condObj, value: val });
  };

  return (
    <Box sx={{ p: 2 }}>

      {/* Section Title */}
      <Typography fontWeight="bold" fontSize={15} sx={{ textAlign: 'center', mb: 2, letterSpacing: 0.5 }}>
        Sidereal Zodiac Criteria
      </Typography>

      {/* Row 1: Planet In Sign At Longitude */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r1.planet} onChange={v => set('r1', 'planet', v)} />
        <Label>In Sign</Label>
        <SignSelect value={forms.r1.sign} onChange={v => set('r1', 'sign', v)} />
        <Label>At Longitude</Label>
        <LonField value={forms.r1.longitude} onChange={v => set('r1', 'longitude', v)} />
        <EventWindowControls nDays={forms.r1.nDays} windowMode={forms.r1.windowMode}
          onNDaysChange={v => set('r1', 'nDays', v)} onModeChange={v => set('r1', 'windowMode', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r1.value} onChange={v => set('r1', 'value', v)} />
        <AddBtn
          disabled={!forms.r1.longitude}
          onClick={() => add('at_longitude_in_sign', { planet: forms.r1.planet, longitude: Number(forms.r1.longitude), sign: forms.r1.sign, nDays: Number(forms.r1.nDays), windowMode: forms.r1.windowMode, value: forms.r1.value })}
        />
      </CriteriaRow>

      {/* Row 2: Planet Planet Declination difference */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r2.planet1} onChange={v => setPair('r2', 'planet1', v, 'planet2')} exclude={[forms.r2.planet2]} />
        <Label>Planet</Label>
        <PlanetSelect value={forms.r2.planet2} onChange={v => setPair('r2', 'planet2', v, 'planet1')} exclude={[forms.r2.planet1]} />
        <Label>Declination difference</Label>
        <DeclField value={forms.r2.diff} onChange={v => set('r2', 'diff', v)} />
        <EventWindowControls nDays={forms.r2.nDays} windowMode={forms.r2.windowMode}
          onNDaysChange={v => set('r2', 'nDays', v)} onModeChange={v => set('r2', 'windowMode', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r2.value} onChange={v => set('r2', 'value', v)} />
        <AddBtn
          disabled={!forms.r2.diff || forms.r2.planet1 === forms.r2.planet2}
          onClick={() => add('declination_difference', { planet1: forms.r2.planet1, planet2: forms.r2.planet2, diff: Number(forms.r2.diff), nDays: Number(forms.r2.nDays), windowMode: forms.r2.windowMode, value: forms.r2.value })}
        />
      </CriteriaRow>

      {/* Row 3: Planet Planet Longitude difference */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r3.planet1} onChange={v => setPair('r3', 'planet1', v, 'planet2')} exclude={[forms.r3.planet2]} />
        <Label>Planet</Label>
        <PlanetSelect value={forms.r3.planet2} onChange={v => setPair('r3', 'planet2', v, 'planet1')} exclude={[forms.r3.planet1]} />
        <Label>Longitude difference</Label>
        <NumField value={forms.r3.diff} onChange={v => set('r3', 'diff', v)} />
        <EventWindowControls nDays={forms.r3.nDays} windowMode={forms.r3.windowMode}
          onNDaysChange={v => set('r3', 'nDays', v)} onModeChange={v => set('r3', 'windowMode', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r3.value} onChange={v => set('r3', 'value', v)} />
        <AddBtn
          disabled={!forms.r3.diff || forms.r3.planet1 === forms.r3.planet2}
          onClick={() => add('longitude_difference', { planet1: forms.r3.planet1, planet2: forms.r3.planet2, diff: Number(forms.r3.diff), nDays: Number(forms.r3.nDays), windowMode: forms.r3.windowMode, value: forms.r3.value })}
        />
      </CriteriaRow>

      {/* Row 4: Planet At Longitude */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r4.planet} onChange={v => set('r4', 'planet', v)} />
        <Label>At Longitude</Label>
        <LonField value={forms.r4.longitude} onChange={v => set('r4', 'longitude', v)} />
        <EventWindowControls nDays={forms.r4.nDays} windowMode={forms.r4.windowMode}
          onNDaysChange={v => set('r4', 'nDays', v)} onModeChange={v => set('r4', 'windowMode', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r4.value} onChange={v => set('r4', 'value', v)} />
        <AddBtn
          disabled={!forms.r4.longitude}
          onClick={() => add('at_longitude', { planet: forms.r4.planet, longitude: Number(forms.r4.longitude), nDays: Number(forms.r4.nDays), windowMode: forms.r4.windowMode, value: forms.r4.value })}
        />
      </CriteriaRow>

      {/* Row 5: Planet At Declination */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r5.planet} onChange={v => set('r5', 'planet', v)} />
        <Label>At Declination</Label>
        <DeclField value={forms.r5.declination} onChange={v => set('r5', 'declination', v)} />
        <EventWindowControls nDays={forms.r5.nDays} windowMode={forms.r5.windowMode}
          onNDaysChange={v => set('r5', 'nDays', v)} onModeChange={v => set('r5', 'windowMode', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r5.value} onChange={v => set('r5', 'value', v)} />
        <AddBtn
          disabled={!forms.r5.declination}
          onClick={() => add('at_declination', { planet: forms.r5.planet, declination: Number(forms.r5.declination), nDays: Number(forms.r5.nDays), windowMode: forms.r5.windowMode, value: forms.r5.value })}
        />
      </CriteriaRow>

      {/* Row 6: Ingress Rashi/Sign */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r6.planet} onChange={v => set('r6', 'planet', v)} />
        <Label>Ingress Rashi/Sign</Label>
        <SignSelect value={forms.r6.sign} onChange={v => set('r6', 'sign', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r6.value} onChange={v => set('r6', 'value', v)} />
        <AddBtn
          onClick={() => add('ingress_rashi', { planet: forms.r6.planet, sign: forms.r6.sign, value: forms.r6.value })}
        />
      </CriteriaRow>

      {/* Row 7: Ingress Navamsha Sign/Rashi */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r7.planet} onChange={v => set('r7', 'planet', v)} />
        <Label>Ingress Navamsha Sign/Rashi</Label>
        <SignSelect value={forms.r7.sign} onChange={v => set('r7', 'sign', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r7.value} onChange={v => set('r7', 'value', v)} />
        <AddBtn
          onClick={() => add('ingress_navamsha', { planet: forms.r7.planet, sign: forms.r7.sign, value: forms.r7.value })}
        />
      </CriteriaRow>

      {/* Row 8: Ingress Nakshatra */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r8.planet} onChange={v => set('r8', 'planet', v)} />
        <Label>Ingress Nakshatra</Label>
        <FormControl size="small" sx={NAKSH_SX}>
          <Select value={forms.r8.nakshatra} onChange={e => set('r8', 'nakshatra', e.target.value)}>
            {NAKSHATRAS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r8.value} onChange={v => set('r8', 'value', v)} />
        <AddBtn
          onClick={() => add('ingress_nakshatra', { planet: forms.r8.planet, nakshatra: forms.r8.nakshatra, value: forms.r8.value })}
        />
      </CriteriaRow>

      {/* Row 9: Combust In the [East/West] */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r9.planet} onChange={v => set('r9', 'planet', v)} exclude={['Sun']} />
        <Label>Combust In the</Label>
        <HemiSelect value={forms.r9.hemisphere} onChange={v => set('r9', 'hemisphere', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r9.value} onChange={v => set('r9', 'value', v)} />
        <AddBtn
          onClick={() => add('combust_in_east', { planet: forms.r9.planet, hemisphere: forms.r9.hemisphere, value: forms.r9.value })}
        />
      </CriteriaRow>

      {/* Row 10: Rising in the [East/West] */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r10.planet} onChange={v => set('r10', 'planet', v)} exclude={['Sun']} />
        <Label>Rising in the</Label>
        <HemiSelect value={forms.r10.hemisphere} onChange={v => set('r10', 'hemisphere', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r10.value} onChange={v => set('r10', 'value', v)} />
        <AddBtn
          onClick={() => add('rising', { planet: forms.r10.planet, hemisphere: forms.r10.hemisphere, value: forms.r10.value })}
        />
      </CriteriaRow>

      {/* Row 11: Retrograde In the [East/West] */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r11.planet} onChange={v => set('r11', 'planet', v)} exclude={['Sun']} />
        <Label>Retrograde In the</Label>
        <HemiSelect value={forms.r11.hemisphere} onChange={v => set('r11', 'hemisphere', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r11.value} onChange={v => set('r11', 'value', v)} />
        <AddBtn
          onClick={() => add('retrograde', { planet: forms.r11.planet, hemisphere: forms.r11.hemisphere, value: forms.r11.value })}
        />
      </CriteriaRow>

      {/* Row 12: Direct in the [East/West] */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r12.planet} onChange={v => set('r12', 'planet', v)} exclude={['Sun']} />
        <Label>Direct in the</Label>
        <HemiSelect value={forms.r12.hemisphere} onChange={v => set('r12', 'hemisphere', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r12.value} onChange={v => set('r12', 'value', v)} />
        <AddBtn
          onClick={() => add('direct', { planet: forms.r12.planet, hemisphere: forms.r12.hemisphere, value: forms.r12.value })}
        />
      </CriteriaRow>

    </Box>
  );
};

MOAGroup1Tab.propTypes = {
  onAddCondition: PropTypes.func.isRequired,
  forms: PropTypes.object,
  onFormsChange: PropTypes.func,
};

export default MOAGroup1Tab;
